import { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import { X, Stethoscope, Info, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  aiSymptomDiagnosis,
  type DiagnosisSuggestion,
  type SymptomDiagnosisResult,
} from '../../lib/api/modules/aiFeatures';
import { safeConsole } from '../../lib/piiProtection';
import { useTranslation } from 'react-i18next';

interface SymptomDiagnosisPanelProps {
  open: boolean;
  onClose: () => void;
  chiefComplaint: string;
  patientId: string;
  /** Visit context — required to persist AI-suggestion consent for the rest of the visit (DPDP/ABDM). */
  visitId?: string;
  /** Apply the chosen diagnosis into the prescription's diagnosis field. */
  onApply: (diagnosis: string) => void;
  /** When true, show the "Save as approved differential" action (caches for all clinics). */
  canApprove?: boolean;
}

const CONFIDENCE_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  HIGH: 'success',
  MEDIUM: 'warning',
  LOW: 'default',
};

/**
 * Chief complaint → ranked differential diagnosis, for physician review before the diagnosis
 * field is filled in. Complements {@link DiagnosisSuggestionsPanel}, which suggests medications
 * FROM a diagnosis — this suggests the diagnosis itself, from symptoms. Nothing is auto-applied;
 * the doctor clicks a suggestion to fill the diagnosis field. Optionally caches a curated,
 * physician-approved differential as a global template for the next doctor (any clinic).
 */
export function SymptomDiagnosisPanel({
  open,
  onClose,
  chiefComplaint,
  patientId,
  visitId,
  onApply,
  canApprove = false,
}: SymptomDiagnosisPanelProps) {
  const { t } = useTranslation('prescriptions');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SymptomDiagnosisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  // DPDP/ABDM: the AI fallback sends real clinical content to Bedrock — the backend requires
  // per-visit consent, acknowledged once and persisted for the rest of the visit. A Tier-1
  // (approved-template) hit never triggers this, since no PHI leaves the system for that path.
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const fetchSuggestions = useCallback(async (consentAcknowledged = false) => {
    if (!chiefComplaint.trim()) {
      setError('Describe the chief complaint / symptoms first');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await aiSymptomDiagnosis.suggest(chiefComplaint.trim(), patientId || undefined, visitId, consentAcknowledged);
      if (res.error) {
        if (res.error.toLowerCase().includes('consent')) {
          setLoading(false);
          setConsentChecked(false);
          setConsentOpen(true);
          return;
        }
        setError(res.error);
        return;
      }
      const data = res.data;
      if (!data || !data.success) {
        setError(data?.errorMessage || 'No suggestions available for this complaint');
        return;
      }
      setResult(data);
      // Pre-select all suggestions by default (doctor can deselect before saving as a template).
      setSelected(new Set(data.suggestions.map((_, i) => i)));
    } catch (err) {
      safeConsole.error('Differential diagnosis suggestion failed:', err);
      setError('AI suggestions are currently unavailable');
    } finally {
      setLoading(false);
    }
  }, [chiefComplaint, patientId, visitId]);

  useEffect(() => {
    if (open) fetchSuggestions();
    else {
      setResult(null);
      setError(null);
      setSelected(new Set());
      setConsentOpen(false);
    }
  }, [open, fetchSuggestions]);

  const confirmConsent = () => {
    setConsentOpen(false);
    fetchSuggestions(true);
  };

  const toggle = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleApply = (s: DiagnosisSuggestion) => {
    onApply(s.icd10Code ? `${s.diagnosis} (${s.icd10Code})` : s.diagnosis);
    toast.success(`Applied "${s.diagnosis}" to Diagnosis`);
    onClose();
  };

  const handleSaveAsTemplate = async () => {
    if (!result) return;
    const lines = (result.suggestions || []).filter((_, i) => selected.has(i));
    if (lines.length === 0) {
      toast.error('Select at least one suggestion to save');
      return;
    }
    setSaving(true);
    try {
      const res = await aiSymptomDiagnosis.approve({
        chiefComplaint: result.chiefComplaint,
        suggestions: lines,
        aiRequestLogId: result.aiRequestLogId,
      });
      if (res.data?.success) {
        toast.success('Saved as approved differential for this complaint');
      } else {
        toast.error(res.data?.errorMessage || 'Failed to save differential');
      }
    } catch (err) {
      safeConsole.error('Save differential failed:', err);
      toast.error('Failed to save differential');
    } finally {
      setSaving(false);
    }
  };

  const isApproved = result?.source === 'APPROVED_TEMPLATE';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Stethoscope size={20} />
          <Typography variant="h6" fontWeight={700}>
            {t('differentialDiagnosis')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            {t('fromComplaint')}
          </Typography>
          <Chip label={chiefComplaint} size="small" color="primary" variant="outlined" />
          {result && (
            isApproved ? (
              <Chip
                icon={<ShieldCheck size={14} />}
                label={t('approvedDifferential')}
                size="small"
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<Sparkles size={14} />}
                label={t('aiSuggestionReviewRequired')}
                size="small"
                color="warning"
                variant="outlined"
              />
            )
          )}
        </Stack>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {!loading && result && result.suggestions.length === 0 && (
          <Alert severity="info">{t('noDifferentialCouldBeSuggestedFor')}</Alert>
        )}

        {!loading && result && result.suggestions.length > 0 && (
          <Stack spacing={1}>
            {result.suggestions.map((s, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.25,
                  border: 1,
                  borderColor: selected.has(i) ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: selected.has(i) ? 'action.selected' : 'transparent',
                }}
              >
                {canApprove && (
                  <Checkbox
                    checked={selected.has(i)}
                    onChange={() => toggle(i)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ p: 0.5, mt: 0.25 }}
                  />
                )}
                <Box
                  onClick={() => handleApply(s)}
                  sx={{ flex: 1, minWidth: 0, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {i + 1}. {s.diagnosis}
                    </Typography>
                    {s.icd10Code && (
                      <Chip label={s.icd10Code} size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                    )}
                    {s.confidence && (
                      <Chip
                        label={s.confidence}
                        size="small"
                        color={CONFIDENCE_COLOR[s.confidence] ?? 'default'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                  </Stack>
                  {s.rationale && (
                    <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
                      <Info size={13} style={{ marginTop: 2, flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary">
                        {s.rationale}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Box>
            ))}

            {result.disclaimer && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {result.disclaimer}
                </Typography>
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              {t('clickASuggestionToFillThe')}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} size="small">
            {t('close')}
          </Button>
          {/* Offer caching only for AI suggestions a doctor has curated. */}
          {canApprove && result && !isApproved && result.suggestions.length > 0 && (
            <Tooltip title={t('cacheThisCuratedDifferentialSoFuture')}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSaveAsTemplate}
                  disabled={saving || selected.size === 0}
                  startIcon={saving ? <CircularProgress size={14} /> : <ShieldCheck size={14} />}
                >
                  {t('saveAsApprovedDifferential')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* AI-suggestion consent (DPDP Act 2023 / ABDM) — required before the AI fallback call */}
      <Dialog open={consentOpen} onClose={() => setConsentOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldCheck size={18} /> <span>{t('patientConsentForAiSuggestions')}</span>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('noApprovedDifferentialExistsForThis')}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />}
            label={t('thePatientHasConsentedToAi')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" disabled={!consentChecked} onClick={confirmConsent}>
            {t('consentGivenContinue')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
